import { CreateShapeResponse, EditShapeResponse } from '@ais-ui/api';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  DrawGeometryType,
  shapeToVectorLayer,
  useDrawFromDialog,
  useDrawingContext,
  useDrawingLifecycle,
  useLayers,
  useOpenLayersContext,
  useResetDrawingContext,
} from '@ais-ui/react-openlayers';
import {
  Button,
  DialogForm,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Label,
  LineColorPickerWithNames,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { useGetShareGroups } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import {
  useFieldArray,
  useForm,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import { GroupMultiSelect } from '../../Groups/GroupMultiSelect';
import { GroupSelect } from '../GroupSelect';
import { grabShapeInitialGroups } from '../../Groups/grabInitialGroups';
import { mutateCallbacks, shapeCallbacks } from '../../utils/mutateCallbacks';
import { UseMutateFunction } from '@tanstack/react-query';
import { useLocalization } from '@ais-ui/locale-management';
import { DrawPointField } from '../DrawFormFields/DrawPointField';
import {
  CircleGeometry,
  geometrySchema,
  getInitialGeometry,
} from '@ais-ui/utils';
import { DrawLineField } from '../DrawFormFields/DrawLineField';
import { DrawPolygonField } from '../DrawFormFields/DrawPolygonField';
import { DrawCircleField } from '../DrawFormFields/DrawCircleField';
import { PiMinusThin, PiPlusThin } from 'react-icons/pi';
import { getArea } from 'ol/sphere';
import GeoJSON from 'ol/format/GeoJSON';

interface SaveShapeCreateDialogProps {
  setShowOuterDialog: React.Dispatch<React.SetStateAction<boolean>>;
  mutate: UseMutateFunction<CreateShapeResponse, unknown, GeoJson, unknown>;
  action: 'create';
  shape?: GeoJson;
  geometryType: DrawGeometryType;
  startInDrawMode: boolean;
}

interface SaveShapeEditDialogProps {
  setShowOuterDialog: React.Dispatch<React.SetStateAction<boolean>>;
  mutate: UseMutateFunction<
    EditShapeResponse,
    unknown,
    { id: string; json: GeoJson },
    unknown
  >;
  action: 'edit';
  shape: GeoJson;
  geometryType: DrawGeometryType;
  startInDrawMode: boolean;
}

type SaveShapeDialogProps =
  | SaveShapeCreateDialogProps
  | SaveShapeEditDialogProps;

const mutateShapeFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string(),
  geometry: geometrySchema,
  parentId: z.string(),
  selectedGroups: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
    })
  ),
  color: z.string(),
  keyValuePairs: z.array(z.object({ key: z.string(), value: z.any() })),
});

export type MutateShapeFormValues = z.infer<typeof mutateShapeFormSchema>;

interface ShapeFormProps {
  form: UseFormReturn<MutateShapeFormValues>;
  onStartDrawing: (
    geometryType: DrawGeometryType,
    shape?: GeoJson | null
  ) => void;
  geometryType: DrawGeometryType;
}

const SaveShapeForm = ({
  form,
  onStartDrawing,
  geometryType,
}: ShapeFormProps) => {
  const { messages } = useLocalization();

  const geometry = form.watch('geometry');

  const colorWatch = useWatch({
    control: form.control,
    name: 'color',
  });

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: 'keyValuePairs',
  });

  const parentId = form.watch('parentId');

  useEffect(() => {
    if (parentId) {
      form.setValue('selectedGroups', []);
    }
  }, [parentId, form]);

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shapesCreateShapeNameLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input id="shape-name" type="text" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="space-y-2">
        <Label>Geometry</Label>
        {geometryType === 'Point' && (
          <DrawPointField
            key={colorWatch}
            form={form}
            geometry={geometry}
            onStartDrawing={onStartDrawing}
            color={colorWatch}
          ></DrawPointField>
        )}
        {geometryType === 'LineString' && (
          <DrawLineField
            key={colorWatch}
            form={form}
            geometry={geometry}
            onStartDrawing={onStartDrawing}
            color={colorWatch}
          ></DrawLineField>
        )}
        {geometryType === 'Polygon' && (
          <DrawPolygonField
            key={colorWatch}
            form={form}
            geometry={geometry}
            onStartDrawing={onStartDrawing}
            color={colorWatch}
          ></DrawPolygonField>
        )}
        {geometryType === 'Circle' && (
          <DrawCircleField
            key={colorWatch}
            form={form}
            geometry={geometry as CircleGeometry}
            onStartDrawing={onStartDrawing}
            color={colorWatch}
          ></DrawCircleField>
        )}
      </div>
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.shapesCreateShapeDescriptionLabel}
              </FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Textarea id="shape-description" {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        key="color"
        control={form.control}
        name="color"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shapesCreateShapeColorLabel}</FormLabel>
              <InfoIconTooltip>
                {messages.shapesCreateShapeColorTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl
              onChange={() => {
                form.clearErrors();
              }}
            >
              <LineColorPickerWithNames
                background={field.value}
                setBackground={(color) => {
                  form.setValue('color', color);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="parentId"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shapesCreateShapeParentIdLabel}</FormLabel>
              <InfoIconTooltip>
                {messages.shapesCreateShapeParentIdTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupSelect value={field.value} onChange={field.onChange} />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="selectedGroups"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shapesCreateShapeShareWithLabel}</FormLabel>
              <InfoIconTooltip>
                {messages.shapesCreateShapeShareWithTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupMultiSelect
                {...field}
                disabled={!!parentId}
                disabledReason={messages.shapeDisabledShareText}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="keyValuePairs"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Metadata</FormLabel>
              <InfoIconTooltip>
                The metadata is used in the SQL report
              </InfoIconTooltip>
            </div>

            <FormControl onChange={() => form.clearErrors()}>
              <>
                {fields.map((field, index) => (
                  <div
                    key={`${field.id}`}
                    className="flex space-x-2 items-center"
                  >
                    <Button
                      type="button"
                      size="icon"
                      disabled={true}
                      variant="ghost"
                      className="h-9 w-9"
                      onClick={() => remove(index)}
                    >
                      {index + 1}
                    </Button>
                    <Input
                      id={`key-${index}`}
                      type="text"
                      placeholder="Enter key"
                      defaultValue={field.key}
                      onChange={(e) => {
                        form.setValue(
                          `keyValuePairs.${index}.key`,
                          e.target.value
                        );
                        fields[index].key = e.target.value;
                      }}
                      className="w-64"
                    />
                    <Input
                      id={`value-${index}`}
                      type="text"
                      placeholder="Enter value"
                      defaultValue={field.value}
                      onChange={(e) => {
                        form.setValue(
                          `keyValuePairs.${index}.value`,
                          e.target.value
                        );
                        fields[index].value = e.target.value;
                      }}
                      className="w-64"
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          className="h-9 w-9"
                          onClick={() => remove(index)}
                        >
                          <PiMinusThin className="h-4 w-4"></PiMinusThin>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Remove key-value pair</TooltipContent>
                    </Tooltip>
                  </div>
                ))}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-9 w-9"
                      onClick={() =>
                        append({
                          key: '',
                          value: '',
                        })
                      }
                    >
                      <PiPlusThin className="h-4 w-4"></PiPlusThin>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Add key-value pair</TooltipContent>
                </Tooltip>
              </>
            </FormControl>
            {form.formState.errors.keyValuePairs && (
              <p className="text-sm font-medium text-destructive">
                Something went wrong with key value pairs
              </p>
            )}
          </FormItem>
        )}
      />
    </>
  );
};

interface ShapeProperties {
  name: string;
  description: string;
  parentId: string;
  acl: string[];
  radius?: number; // Optional radius property
  [key: string]: any; // key value pairs
}

export function SaveShapeDialog({
  action,
  setShowOuterDialog,
  geometryType,
  shape,
  mutate,
  startInDrawMode,
}: SaveShapeDialogProps) {
  const { messages } = useLocalization();
  const { removeLayerById, addLayer, doesLayerExist } = useLayers();
  const drawingContext = useDrawingContext();
  const resetDrawingContext = useResetDrawingContext();
  const { map } = useOpenLayersContext();
  const { onFinishDrawing } = useDrawingLifecycle();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();

  const { toast } = useToast();

  const knownKeys = [
    'name',
    'acl',
    'description',
    'parentId',
    'id',
    'radius',
    'owner',
    'user_id',
    'color',
  ];

  const form = useForm<MutateShapeFormValues>({
    resolver: zodResolver(mutateShapeFormSchema),
    defaultValues: {
      name: shape?.properties.name || '',
      geometry: getInitialGeometry(shape, geometryType),
      description: shape?.properties.description || '',
      parentId: shape?.properties?.parentId || '',
      color: shape?.properties.color || '#003b5c',
      selectedGroups: grabShapeInitialGroups(shape, groups),
      keyValuePairs: Object.entries(shape?.properties || {})
        .filter(([key]) => !knownKeys.includes(key))
        .map(([key, value]) => ({ key, value })),
    },
  });

  // The user will expect
  useEffect(() => {
    const originalOnCancel = drawingContext.onCancel.current;

    const onCancelDrawingWithDialogClose = () => {
      if (originalOnCancel) {
        originalOnCancel(); // Call the original onCancel function instead of drawingContext.onCancel.current
      }
      setShowOuterDialog(false);
    };

    drawingContext.onCancel.current = onCancelDrawingWithDialogClose;
  }, [drawingContext, setShowOuterDialog]);

  const [showDialog, setShowDialog] = useState<boolean>(false);

  const { showDrawPanel } = useDrawingContext();

  const handleOpenChange = (isOpen: boolean) => {
    setShowDialog(isOpen); // Update showDialog state

    // Make sure that we are not unmounting the component when drawing, but we will unmount it if we are not drawing
    if (!showDrawPanel) {
      setShowOuterDialog(isOpen);
    }
  };

  const { onStartDrawingFromDialog } = useDrawFromDialog(form, setShowDialog);

  // Start drawing when the dialog is opened
  useEffect(() => {
    if (startInDrawMode) {
      onStartDrawingFromDialog(geometryType, shape);
    } else {
      setShowDialog(true);
    }
  }, []);

  const [dialogTitle, dialogDescription] =
    action === 'edit'
      ? [messages.shapesEditShapeTitle, messages.shapesEditShapeDescription]
      : [
          messages.shapesCreateShapeTitle,
          messages.shapesCreateShapeDescription,
        ];

  const onSubmit = (data: MutateShapeFormValues) => {
    onFinishDrawing();

    if (!data.geometry) {
      return;
    }

    let radius = null;
    if (data?.geometry?.type === 'Point' && 'radius' in data.geometry) {
      radius = (data.geometry as CircleGeometry).radius;
    }

    const keyValueObject = data.keyValuePairs.reduce((acc, pair) => {
      acc[pair.key] = pair.value;
      return acc;
    }, {} as { [key: string]: any });
    let area = null;
    let areaUnit = null;
    if (data.geometry.type === 'Polygon') {
      const format = new GeoJSON();
      const olGeometry = format.readGeometry(data.geometry);

      // Calculate area using the spherical method on the transformed geometry
      const areaInSquareMeters = getArea(olGeometry, {
        projection: 'EPSG:4326',
      });

      // Always convert to square kilometers with 2 decimal places
      area = Math.round((areaInSquareMeters / 1000000) * 100) / 100;
      areaUnit = 'km²';
    }

    const properties: ShapeProperties = {
      name: data.name,
      description: data.description,
      parentId: data.parentId,
      acl: data.selectedGroups.map((group) => group.value),
      color: data.color,
      ...keyValueObject,
    };

    if (radius) {
      properties['radius'] = radius;
    }

    if (area !== null && areaUnit !== null) {
      properties['area'] = `${area} ${areaUnit}`;
    }

    const geoJson = {
      type: 'Feature',
      properties: properties,
      geometry: data.geometry,
    } as GeoJson;

    setIsSubmitting(true);

    const callbacks = shapeCallbacks(
      toast,
      messages,
      action,
      setIsSubmitting,
      setShowDialog
    );
    const extendedOnSuccess = () => {
      callbacks.onSuccess();

      if (drawingContext.geometryType) {
        resetDrawingContext();
      }
      setShowOuterDialog(false);
    };

    const extendedCallbacks = {
      ...callbacks,
      onSuccess: extendedOnSuccess,
    };

    if (action === 'edit') {
      const shapeName = shape.properties.name;
      const id = shape?.properties.id;
      if (!id) {
        toast({
          className: 'save-shape-error-toast',
          title: 'Error!',
          description: `Failed to edit the shape ${shapeName}, missing id.`,
          variant: 'destructive',
        });
        setShowDialog(false);
        return;
      }
      geoJson.properties.id = id; // need to be set here otherwise it won't be shown on the map
      mutate({ id, json: geoJson }, extendedCallbacks);

      if (doesLayerExist(id)) {
        removeLayerById(id);
        const layer = shapeToVectorLayer(geoJson);
        const name = shape?.properties?.name
          ? shape.properties.name
          : 'No name';

        addLayer(
          layer,
          name,
          true,
          true,
          true,
          geoJson.properties,
          geoJson.properties.id
        );
      }
    } else {
      mutate(geoJson, extendedCallbacks);
    }
  };

  return (
    <Form {...form}>
      <DialogForm
        showDialog={showDialog}
        onOpenChange={handleOpenChange}
        title={dialogTitle}
        description={dialogDescription}
        isLoading={isLoadingGroups}
        hasLoaded={!isLoadingGroups && !!groups}
        buttonId="shape-confirm-save-button"
        buttonText={
          isSubmitting ? messages.genericSubmitting : messages.genericSubmit
        }
        buttonDisabled={isSubmitting}
        buttonOnClick={form.handleSubmit(onSubmit)}
        observeResize={false}
      >
        <SaveShapeForm
          form={form}
          onStartDrawing={onStartDrawingFromDialog}
          geometryType={geometryType}
        />
      </DialogForm>
    </Form>
  );
}
